/**
 * Extruder screw-speed thresholding — the one place the bands are defined.
 *
 * Both lamination lines, in both places they are judged (the extruder
 * dashboards' run-event tables and the overview diagrams), classify screw
 * speed here, against the operator-tuned row in `settings_extruders`. That row
 * is plant-wide, so a change on the Settings page moves every extruder color
 * and every run event on both lines together.
 *
 * Two callers, one rule: the diagrams classify a single instantaneous reading
 * with `screwBand`, while the tables walk a series and use
 * `classifyWithHysteresis` so a speed hovering on a boundary does not produce a
 * run of spurious events. Both share the same boundaries.
 */

export type ScrewThresholds = {
  idle_min: number; // boundary between stopped and idle (Hz)
  running_min: number; // boundary between idle and running (Hz)
  hysteresis: number; // 0.2 => 20% bands
};

export type ScrewState = "stopped" | "idle" | "running";

/**
 * Fallback only — used until `/settings/extruders` answers, or if it fails.
 * The stored row is the real source; see `useExtruderThresholds`.
 */
export const DEFAULT_THRESHOLDS: ScrewThresholds = {
  idle_min: 2,
  running_min: 18,
  hysteresis: 0,
};

/** One reading's band. At or above running_min → running; at or above idle_min
 *  → idle; below that → stopped, including a screw barely creeping. */
export const screwBand = (hz: number, t: ScrewThresholds): ScrewState =>
  hz >= t.running_min ? "running" : hz >= t.idle_min ? "idle" : "stopped";

/**
 * Hysteretic classifier (Schmitt trigger) for a series:
 * - stop<->idle uses idle_min ± (idle_min*hyst)
 * - idle<->running uses running_min ± (running_min*hyst)
 *
 * With hysteresis 0 this reduces to `screwBand` on every sample.
 */
export const classifyWithHysteresis = (
  hz: number,
  prev: ScrewState,
  t: ScrewThresholds
): ScrewState => {
  const iLo = t.idle_min * (1 - t.hysteresis);
  const iHi = t.idle_min * (1 + t.hysteresis);
  const rLo = t.running_min * (1 - t.hysteresis);
  const rHi = t.running_min * (1 + t.hysteresis);

  // The downward tests are strict so that a boundary speed belongs to the same
  // band `screwBand` gives it. With inclusive tests on both sides and no
  // hysteresis the two edges coincide, and a screw sitting exactly on idle_min
  // flip-flops stopped/idle every sample — a run of phantom events. Any
  // hysteresis above zero separates the edges and this stops mattering.
  switch (prev) {
    case "stopped":
      // require a clear move above idle + band to leave stopped
      return hz >= iHi ? "idle" : "stopped";
    case "idle":
      if (hz < iLo) return "stopped";
      if (hz >= rHi) return "running";
      return "idle";
    case "running":
      // require a clear move below running - band to leave running
      return hz < rLo ? "idle" : "running";
    default:
      return screwBand(hz, t);
  }
};

/** Coerce a `/settings/extruders` response into a complete threshold set. */
export const thresholdsFrom = (
  j?: Partial<ScrewThresholds> | null
): ScrewThresholds => ({
  idle_min: Number(j?.idle_min ?? DEFAULT_THRESHOLDS.idle_min),
  running_min: Number(j?.running_min ?? DEFAULT_THRESHOLDS.running_min),
  hysteresis: Number(j?.hysteresis ?? DEFAULT_THRESHOLDS.hysteresis),
});
